import {subMonths} from 'date-fns';
import {stageStepsMapping, successStepLogId} from '../../constants';
import texts from '../../texts';
import {ReminderTypes} from './enum';
import {getUsername} from '../../../../_shared/GWTBridge/invoke/genPage';
import {getCurrentDateForEOYReminders, getReminderPostponedDate} from '../../actions/reminders';
import {common} from '@priority-software/priority-proto-api/lib';

export function getStepName(logId: string) {
    if (!logId) return '';
    const key = stageStepsMapping
        .reduce((prev, acc) => [...prev, ...acc.steps], [])
        .find(step => step.logId === logId)?.name;
    return key ? texts[key] : '';
}

export function getStages(currentWizardStepID: string) {
    const activeStageIndex = stageStepsMapping.findIndex(stage =>
        stage.steps.some(step => step.logId === currentWizardStepID)
    );

    return stageStepsMapping.map((_, index) => ({
        title: texts[`reminder_stage_${index}_title`],
        text: texts[`reminder_stage_${index}_instructions`],
        name: texts[`reminder_stage_${index}_name`],
        isActive: index === activeStageIndex,
        isComplete: index < activeStageIndex,
        isDisabled: index > activeStageIndex
    }));
}

export const isFirstStageStepCondition = (
    stageIndex: number,
    currentWizardStep: string,
    isOwner: boolean
) => {
    return stageStepsMapping[stageIndex].steps[1]?.logId === currentWizardStep && isOwner;
};

export const getNewFiscalYearStartDate = (fiscalYearStart: common.IDate) =>
    new Date(fiscalYearStart.year, fiscalYearStart.month - 1, fiscalYearStart.day);

export const getLastFiscalYearDay = (fiscalYearEnd: common.IDate) =>
    new Date(fiscalYearEnd.year, fiscalYearEnd.month - 1, fiscalYearEnd.day);

type GetReminderTypeParams = {
    activeStageIndex: number;
    currentWizardStep: string;
    prevStepUser?: string;
    stepAssignBy?: string;
    isOwner: boolean;
    currentEndDate?: common.IDate;
    newStartDate?: common.IDate;
};

export function getReminderType(params: GetReminderTypeParams) {
    const {
        activeStageIndex,
        currentWizardStep,
        prevStepUser = '',
        stepAssignBy = '',
        isOwner,
        currentEndDate,
        newStartDate
    } = params;
    if (!currentEndDate || !newStartDate) return ReminderTypes.default;

    const currentDate = getCurrentDateForEOYReminders();
    const lastFiscalYearDay = getLastFiscalYearDay(currentEndDate);
    const newFiscalYearStart = getNewFiscalYearStartDate(newStartDate);

    if (stepAssignBy && stepAssignBy !== getUsername() && !isOwner) {
        return ReminderTypes.stepAssigned;
    } else if (prevStepUser && prevStepUser !== getUsername()) {
        const isFinalStep = currentWizardStep === successStepLogId;

        // check if the current step is the first step of next stage
        const firstStageCompletedCond =
            isFirstStageStepCondition(1, currentWizardStep, isOwner) &&
            currentDate < lastFiscalYearDay;
        const secondStageCompletedCond =
            isFirstStageStepCondition(2, currentWizardStep, isOwner) &&
            currentDate < newFiscalYearStart;

        if (firstStageCompletedCond || secondStageCompletedCond || isFinalStep) {
            return ReminderTypes.stageCompleted;
        }
        return ReminderTypes.stepCompleted;
    } else if (stageStepsMapping[activeStageIndex]) {
        return isFirstStageStepCondition(activeStageIndex, currentWizardStep, isOwner)
            ? ReminderTypes.wizardStage
            : ReminderTypes.stepAwaiting;
    }
    return ReminderTypes.default;
}

export function displayReminderForUpcomingYear(date: common.IDate) {
    const currentDate = getCurrentDateForEOYReminders();
    const monthBeforeFiscalYearEnd = subMonths(new Date(date.year, date.month - 1, date.day), 1);
    return currentDate >= monthBeforeFiscalYearEnd;
}

export function displayReminder(
    stageIndex: number,
    currentWizardStepID: string,
    fiscalYearStart: common.IDate,
    fiscalYearEnd: common.IDate,
    type: ReminderTypes
) {
    const currentDate = getCurrentDateForEOYReminders();
    if (!fiscalYearStart || !fiscalYearEnd) return false;

    const reminderPostponedDate = getReminderPostponedDate(currentWizardStepID);
    if (new Date(reminderPostponedDate) > currentDate) {
        // if user clicked `Remind me later` button, then postpone the reminder for 1 day
        return false;
    }

    if (stageIndex === 0) {
        const monthBeforeFiscalYearEnd = subMonths(
            new Date(fiscalYearEnd.year, fiscalYearEnd.month - 1, fiscalYearEnd.day),
            1
        );

        return currentDate >= monthBeforeFiscalYearEnd;
    } else if (stageIndex === 1) {
        const lastFiscalYearDay = getLastFiscalYearDay(fiscalYearEnd);
        // show special reminder if the user is the owner of the EOY and the last step of the first stage is completed by another user
        if (type === ReminderTypes.stageCompleted) return true;

        return currentDate >= lastFiscalYearDay;
    } else if (stageIndex === 2) {
        const newFiscalYearStart = getNewFiscalYearStartDate(fiscalYearStart);
        // show special reminder if the user is the owner of the EOY and the last step of the first stage is completed by another user
        if (type === ReminderTypes.stageCompleted) return true;

        return currentDate >= newFiscalYearStart;
    }
    return false;
}

export const getCompletedStageText = (stageIndex: number) => {
    if (stageIndex === 0) {
        return texts.stage0_step4_title + '\n' + texts.stage0_step4_prompt;
    } else if (stageIndex === 1) {
        console.log(2244);
        return texts.stage1_step5_title + '\n' + texts.stage1_step5_prompt;
    } else if (stageIndex === 2) {
        return '';
    }
    return '';
};

export const getIsDateInRange = () => {
    const urlParams = window.location.search;
    if (urlParams.includes('_ignoreEOYPromoDate')) {
        return true;
    }
    const today = new Date();

    // Check if today is Monday (1) or Thursday (4)
    const dayOfWeek = today.getDay();
    const isMondayOrThursday = dayOfWeek === 1 || dayOfWeek === 4;

    if (!isMondayOrThursday) {
        return false;
    }

    // Get current month and day
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    // Check if date is in the range December 28 - January 20
    const isInDateRange =
        (currentMonth === 11 && currentDay >= 28) || // December 28-31
        (currentMonth === 0 && currentDay <= 20); // January 1-20

    return isInDateRange;
};
